import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { appRoutes } from 'app/app-routes';
import { formValidationSchema } from 'common/functions/form-validation-schema';
import { useMakes } from 'common/hooks/useMakes';
import { useModels } from 'common/hooks/useModels';
import { selectDistanceOptions } from 'common/hooks/useSelectOptions';
import { useSearchDistance } from 'common/hooks/useZipCodeValidation';
import wsCookies from 'common/tools/ws-cookies';
import { Formik, FormikProps } from 'formik';
import React, { PropsWithChildren, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';
import { searchParamActions, searchParamSelectors } from 'redux/search-param.slice';
import { WsFormTextField } from 'stories/forms/WsFormTextField';
import { WsButton } from 'stories/inputs/WsButton';
import { WsCheckbox } from 'stories/inputs/WsCheckbox';
import { WsNativeSelect } from 'stories/inputs/WsNativeSelect';
import WsFlexbox from 'stories/layout/WsFlexbox';
import * as yup from 'yup';

interface Props {
  sx?: any;
}

interface FormValues {
  zipCode: string;
}

const HomeSearch = (props: PropsWithChildren<Props>) => {
  const dispatch = useDispatch();
  const [redirectToResults, setNavigateToResults] = useState(false);
  const { makeOptions, selectedMake, setSelectedMakes } = useMakes();
  const { getModelOptions, getSelectedModel, setSelectedModels } = useModels();
  const makesPopularFilter = useSelector((state: RootState) => cacheSelectors.makesPopularFilter(state));
  const distance = useSelector((state: RootState) => searchParamSelectors.distance(state));
  const urlParams = useSelector((state: RootState) => searchParamSelectors.urlParams(state));
  const zipCodeObj = useSelector((state: RootState) => searchParamSelectors.zipCodeObj(state));

  const allMakeOptions = React.useMemo(() => [...[{ label: 'All Makes', id: '' }], ...makeOptions], [makeOptions]);

  const validationSchema = React.useMemo(
    () =>
      yup.object().shape({
        zipCode: formValidationSchema.zipCode,
      }),
    [],
  );
  const { searchZipCodeValidate } = useSearchDistance<FormValues>(validationSchema);

  const handleMakeChange = (id: string) => {
    if (id === '') setSelectedMakes([]);
    else {
      setSelectedMakes([id], true);
    }
  };

  const getAllModelOptions = (selectedMake: string) => {
    return [...[{ label: 'All Models', id: '' }], ...(selectedMake.length ? getModelOptions(selectedMake) : [])];
  };

  const handleModelChange = (id: string) => {
    if (id === '') setSelectedModels(selectedMake, []);
    else {
      setSelectedModels(selectedMake, [id], true);
    }
  };

  const handleDistanceChange = (id: string) => {
    dispatch(searchParamActions.distance(id));
  };

  const handleMakesPopularFilterChange = (checked: boolean) => {
    dispatch(cacheActions.makesPopularFilter(!checked));
  };

  const doSearch = () => {
    setNavigateToResults(true);
  };

  if (redirectToResults) {
    return <Navigate to={`${appRoutes.search}?${urlParams}`} />;
  }

  return (
    <Box
      sx={{
        ...props.sx,
      }}
    >
      <WsFlexbox>
        <Typography
          sx={{
            paddingLeft: 1,
            paddingBottom: '2px',
            fontWeight: 600,
            flexGrow: 1,
          }}
          variant="h5"
        >
          Search Used Cars for Sale
        </Typography>
      </WsFlexbox>
      <Formik
        validationSchema={validationSchema}
        validate={searchZipCodeValidate}
        initialValues={{ zipCode: zipCodeObj?.code || '' }}
        validateOnChange={false}
        enableReinitialize={true}
        onSubmit={() => doSearch()}
      >
        {(props: FormikProps<FormValues>) => (
          <form onSubmit={props.handleSubmit}>
            <WsFlexbox
              sx={{
                // marginTop: 1,
                gap: 2,
                minHeight: '63px',
              }}
            >
              <WsFlexbox
                sx={{
                  gap: 2,
                  position: 'relative',
                  top: '9px',
                }}
              >
                <WsNativeSelect
                  width={300}
                  id="make"
                  data={allMakeOptions}
                  value={selectedMake}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => handleMakeChange(event.target.value)}
                />
                <WsNativeSelect
                  width={300}
                  id="model"
                  data={getAllModelOptions(selectedMake)}
                  value={getSelectedModel(selectedMake)}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => handleModelChange(event.target.value)}
                />
                <WsNativeSelect
                  width={160}
                  id="distance"
                  data={selectDistanceOptions}
                  value={distance}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => handleDistanceChange(event.target.value)}
                />
              </WsFlexbox>
              <WsFormTextField id="zipCode" label="Zip Code" width={80} formik={props} useCheck />
              <WsButton label="Search" color="secondary" size="large" type="submit" />
            </WsFlexbox>
          </form>
        )}
      </Formik>
      <Box sx={{ position: 'relative', top: '-20px', left: '8px' }}>
        <WsCheckbox
          sx={{ marginLeft: 1 }}
          checked={!makesPopularFilter}
          onChange={handleMakesPopularFilterChange}
          label="Show All Makes"
        />
      </Box>
    </Box>
  );
};

export default HomeSearch;
